import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Clock,
  Grid3X3,
  RotateCcw,
  Search,
  Trash2,
  X,
  Inbox,
  Eye,
  AlignLeft,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useMessage } from '../../context/MessageContext';
import { getContentHistory, getContentTaskStatus, deleteContentTask, createContentTask, regenerateImage, downloadTaskImages } from '../../services/contentService';
import { copyToClipboard } from '../../services/formatterService';
import { stripMarkdownForXHS } from '../../components/shared/ContentFormatters';
import ImagePreviewModal from '../../components/modals/ImagePreviewModal';
import ContentWizardModal from '../../components/modals/ContentWizardModal';
import TaskPreviewModal from './TaskPreviewModal';
import TaskDetailModal from './TaskDetailModal';
import {
  FILTERS,
  MOCK_TASKS,
  isMockTask,
  getStatusMeta,
  formatTime,
  formatSmartTime,
  normalizeTaskPages,
  getTaskTitle,
  getTaskCover,
  guessPlatformByTask,
  sanitizeFilename,
  downloadTextFile,
} from './historyUtils';
import './ContentHistory.css';

/* ── Delete Confirmation Dialog ── */
const DeleteConfirmDialog = ({ task, onConfirm, onCancel }) => {
  if (!task) return null;
  return (
    <div className="ch-confirm-overlay">
      <div className="ch-confirm-backdrop" onClick={onCancel} />
      <div className="ch-confirm-card">
        <div className="ch-confirm__icon">
          <Trash2 style={{ width: 22, height: 22 }} />
        </div>
        <h4 className="ch-confirm__title">确定删除此任务？</h4>
        <p className="ch-confirm__desc">
          「{getTaskTitle(task)}」将被永久删除，此操作不可恢复。
        </p>
        <div className="ch-confirm__btns">
          <button type="button" className="ch-confirm__btn ch-confirm__btn--cancel" onClick={onCancel}>
            取消
          </button>
          <button type="button" className="ch-confirm__btn ch-confirm__btn--danger" onClick={onConfirm}>
            确认删除
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Status Badge ── */
const StatusBadge = ({ status }) => {
  const meta = getStatusMeta(status);
  return (
    <span className={`ch-status ch-status--${status || 'unknown'}`}>
      <span className="ch-status__dot" />
      {meta.label}
    </span>
  );
};

/* ── Main Component ── */
const ContentHistory = () => {
  const { token, user } = useAuth();
  const { showMessage } = useMessage();

  const isTestUser = Boolean(
    user && (
      String(user.phone || '').startsWith('test') ||
      String(user.username || '').toLowerCase().startsWith('test')
    )
  );

  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState('card'); // 'card' | 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all'); // 'all' | 'xiaohongshu' | 'wechat'
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [draftWizard, setDraftWizard] = useState(null); // draft task to edit in wizard
  const searchInputRef = useRef(null);
  const pollingTasksRef = useRef(new Set());

  const RUNNING_STATUSES = useMemo(() => new Set(['queued', 'running']), []);

  const upsertTaskDetail = useCallback((detail) => {
    if (!detail?.task_id) return;
    setSelectedTask((prev) => {
      if (!prev || prev.task_id !== detail.task_id) return prev;
      return detail;
    });
    setTasks((prev) => prev.map((item) => (item.task_id === detail.task_id ? detail : item)));
  }, []);

  const patchTaskStatus = useCallback((taskId, status) => {
    if (!taskId || !status) return;
    setSelectedTask((prev) => {
      if (!prev || prev.task_id !== taskId) return prev;
      return {
        ...prev,
        status,
        error_code: '',
        error_message: '',
      };
    });
    setTasks((prev) => prev.map((item) => (
      item.task_id === taskId
        ? { ...item, status, error_code: '', error_message: '' }
        : item
    )));
  }, []);

  const startTaskStatusPolling = useCallback((taskId, options = {}) => {
    if (!token || !taskId || pollingTasksRef.current.has(taskId)) return;

    const intervalMs = Number(options.intervalMs || 1500);
    const maxAttempts = Number(options.maxAttempts || 30);
    const silent = Boolean(options.silent ?? true);
    pollingTasksRef.current.add(taskId);

    const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

    (async () => {
      try {
        for (let i = 0; i < maxAttempts; i += 1) {
          const detail = await getContentTaskStatus(taskId, token);
          upsertTaskDetail(detail);
          if (!RUNNING_STATUSES.has(detail.status)) break;
          await sleep(intervalMs);
        }
      } catch (error) {
        if (!silent) showMessage('warning', error?.message || '任务状态同步失败');
      } finally {
        pollingTasksRef.current.delete(taskId);
      }
    })();
  }, [RUNNING_STATUSES, showMessage, token, upsertTaskDetail]);

  /* ── Data Fetching ── */
  const fetchHistory = useCallback(async () => {
    if (!token) { setTasks([]); return; }
    if (!user) return;

    setLoading(true);
    try {
      const data = await getContentHistory(token);
      const list = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
      setTasks(isTestUser ? [...MOCK_TASKS, ...list] : list);
    } catch (error) {
      if (isTestUser) {
        setTasks(MOCK_TASKS);
      } else {
        showMessage('error', error?.message || '获取历史任务失败');
        setTasks([]);
      }
    } finally {
      setLoading(false);
    }
  }, [showMessage, token, isTestUser, user]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  /* ── Filtering + Search ── */
  const filteredTasks = useMemo(() => {
    let result = tasks;

    // Tab filter
    if (activeTab === 'all') {
      result = result.filter((t) => t.status !== 'draft');
    } else if (activeTab === 'completed') {
      result = result.filter((t) => t.status === 'succeeded' || t.status === 'partial_failed');
    } else if (activeTab === 'draft') {
      result = result.filter((t) => t.status === 'draft');
    }

    // Platform filter
    if (platformFilter !== 'all') {
      result = result.filter((t) => guessPlatformByTask(t) === platformFilter);
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((t) => {
        const title = getTaskTitle(t).toLowerCase();
        const topic = String(t.topic || '').toLowerCase();
        const tags = Array.isArray(t.tags) ? t.tags.join(' ').toLowerCase() : '';
        return title.includes(q) || topic.includes(q) || tags.includes(q);
      });
    }

    return result;
  }, [activeTab, tasks, searchQuery, platformFilter]);

  const selectedTaskPages = useMemo(() => normalizeTaskPages(selectedTask), [selectedTask]);
  const onlyMockTasks = useMemo(() => filteredTasks.length > 0 && filteredTasks.every(isMockTask), [filteredTasks]);

  /* ── Task Detail ── */
  const openTaskDetail = useCallback(async (task) => {
    // Draft tasks → open wizard for editing
    if (task.status === 'draft') {
      setDraftWizard(task);
      return;
    }
    setSelectedTask(task);
    if (!token || isMockTask(task) || !task?.task_id) return;

    setDetailLoading(true);
    try {
      const detail = await getContentTaskStatus(task.task_id, token);
      upsertTaskDetail(detail);
      if (RUNNING_STATUSES.has(detail.status)) {
        startTaskStatusPolling(detail.task_id, { silent: true });
      }
    } catch (error) {
      showMessage('warning', error?.message || '读取任务详情失败，已展示当前缓存数据');
    } finally {
      setDetailLoading(false);
    }
  }, [RUNNING_STATUSES, showMessage, startTaskStatusPolling, token, upsertTaskDetail]);

  const refreshDetail = useCallback(async () => {
    if (!selectedTask) return;
    if (isMockTask(selectedTask)) { showMessage('info', '模拟任务无需刷新'); return; }
    if (!selectedTask?.task_id || !token) return;

    setDetailLoading(true);
    try {
      const detail = await getContentTaskStatus(selectedTask.task_id, token);
      upsertTaskDetail(detail);
      if (RUNNING_STATUSES.has(detail.status)) {
        startTaskStatusPolling(detail.task_id, { silent: true });
      }
      showMessage('success', '任务详情已刷新');
    } catch (error) {
      showMessage('error', error?.message || '刷新详情失败');
    } finally {
      setDetailLoading(false);
    }
  }, [RUNNING_STATUSES, selectedTask, showMessage, startTaskStatusPolling, token, upsertTaskDetail]);

  const closeDetail = () => {
    setPreviewOpen(false);
    setSelectedTask(null);
  };

  const handleRegenerateImage = useCallback(async (taskId, pageNumber) => {
    if (!token || !taskId) return;

    // 立即给 UI 一个“处理中”状态，避免必须手动刷新才看到状态变化
    patchTaskStatus(taskId, 'running');
    setDetailLoading(true);
    try {
      await regenerateImage(taskId, pageNumber, token, { usePageNumber: true });
      const detail = await getContentTaskStatus(taskId, token);
      upsertTaskDetail(detail);
      if (RUNNING_STATUSES.has(detail.status)) {
        startTaskStatusPolling(taskId, { silent: true });
      }
    } finally {
      setDetailLoading(false);
    }
  }, [RUNNING_STATUSES, patchTaskStatus, startTaskStatusPolling, token, upsertTaskDetail]);

  /* ── Copy & Download ── */
  const handlePreviewImage = (index, images) => {
    if (!images || images.length === 0) return;
    setPreviewImages(images);
    setPreviewIndex(typeof index === 'number' ? index : 0);
  };

  const copyTaskText = useCallback(async () => {
    if (!selectedTask) return;
    const title = getTaskTitle(selectedTask);
    const tags = Array.isArray(selectedTask.tags)
      ? selectedTask.tags.map((tag) => `#${String(tag).replace(/^#/, '')}`).join(' ')
      : '';
    const rawBody = selectedTask.copywriting || '';
    const body = stripMarkdownForXHS(rawBody);
    const fullText = `${title}\n\n${body}${tags ? `\n\n${tags}` : ''}`;
    const ok = await copyToClipboard(fullText);
    showMessage(ok ? 'success' : 'error', ok ? '文案已复制' : '复制失败');
  }, [selectedTask, showMessage]);

  const downloadTaskAssets = useCallback(async (task) => {
    if (!task || !token) return;
    try {
      showMessage('info', '正在打包图片...');
      await downloadTaskImages(task.task_id, token);
      showMessage('success', '图片打包下载完成');
    } catch (err) {
      showMessage('error', err?.message || '下载失败');
    }
  }, [token, showMessage]);

  /* ── Delete ── */
  const handleDeleteClick = useCallback((e, task) => {
    e.stopPropagation();
    setDeleteTarget(task);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    if (isMockTask(deleteTarget)) {
      setTasks((prev) => prev.filter((t) => t.task_id !== deleteTarget.task_id));
      setDeleteTarget(null);
      showMessage('success', '已删除模拟任务');
      return;
    }
    if (!token) return;

    setDeleting(true);
    try {
      await deleteContentTask(deleteTarget.task_id, token);
      setTasks((prev) => prev.filter((t) => t.task_id !== deleteTarget.task_id));
      showMessage('success', '任务已删除');
    } catch (error) {
      showMessage('error', error?.message || '删除失败');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, token, showMessage]);

  /* ── Redo (re-create from same topic/skill) ── */
  const handleRedoClick = useCallback(async (e, task) => {
    e.stopPropagation();
    if (isMockTask(task)) {
      showMessage('info', '模拟任务不支持重做');
      return;
    }
    if (!token || !task?.topic || !task?.skill_name) {
      showMessage('warning', '缺少原任务信息，无法重做');
      return;
    }

    try {
      showMessage('info', '正在重新提交任务…');
      await createContentTask(task.topic, task.skill_name, token);
      showMessage('success', '任务已重新提交，稍后刷新查看');
      fetchHistory();
    } catch (error) {
      showMessage('error', error?.message || '重做失败');
    }
  }, [token, showMessage, fetchHistory]);

  /* ── List sorting by date ── */
  const sortedListTasks = useMemo(() => {
    return [...filteredTasks].sort((a, b) => {
      const timeA = new Date(a.created_at || 0).getTime();
      const timeB = new Date(b.created_at || 0).getTime();
      return timeB - timeA; // Descending: newest first
    });
  }, [filteredTasks]);

  /* ═══════════════ RENDER ═══════════════ */
  return (
    <div className="ch-page animate-fade-in">
      <div className="ch-content">

        {/* ── Header ── */}
        <header className="ch-header">
          <div className="ch-tabs">
            {FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveTab(item.id)}
                className={`ch-tab ${activeTab === item.id ? 'ch-tab--active' : ''}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="ch-controls">
            {/* Platform Filter */}
            <div className="ch-platform-filter">
              {[
                { id: 'all', label: '全部' },
                { id: 'xiaohongshu', label: '小红书' },
                { id: 'wechat', label: '公众号' },
              ].map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlatformFilter(p.id)}
                  className={`ch-platform-btn ch-platform-btn--${p.id} ${platformFilter === p.id ? 'ch-platform-btn--active' : ''}`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* View Toggle */}
            <div className="ch-view-toggle">
              <button
                type="button"
                title="卡片视图"
                onClick={() => setViewMode('card')}
                className={`ch-view-btn ${viewMode === 'card' ? 'ch-view-btn--active' : ''}`}
              >
                <Grid3X3 style={{ width: 16, height: 16 }} />
              </button>
              <button
                type="button"
                title="列表视图"
                onClick={() => setViewMode('list')}
                className={`ch-view-btn ${viewMode === 'list' ? 'ch-view-btn--active' : ''}`}
              >
                <AlignLeft style={{ width: 16, height: 16 }} />
              </button>
            </div>

            {/* Expandable Search */}
            <div
              className={`ch-search-expandable ${searchExpanded || searchQuery ? 'ch-search-expandable--open' : ''}`}
              onMouseEnter={() => setSearchExpanded(true)}
              onMouseLeave={() => {
                if (!searchQuery && document.activeElement !== searchInputRef.current) {
                  setSearchExpanded(false);
                }
              }}
            >
              <button
                type="button"
                className="ch-search-expandable__trigger"
                onClick={() => {
                  setSearchExpanded(true);
                  setTimeout(() => searchInputRef.current?.focus(), 100);
                }}
              >
                <Search style={{ width: 16, height: 16 }} />
              </button>
              <div className="ch-search-expandable__field">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="搜索标题、主题或标签…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchExpanded(true)}
                  onBlur={() => {
                    if (!searchQuery) setSearchExpanded(false);
                  }}
                  className="ch-search-expandable__input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="ch-search-expandable__clear"
                    onClick={() => {
                      setSearchQuery('');
                      searchInputRef.current?.focus();
                    }}
                  >
                    <X style={{ width: 14, height: 14 }} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* ── Mock Banner ── */}
        {onlyMockTasks && (
          <div className="ch-mock-banner">
            当前展示的是模拟任务数据，用于前端联调与交互测试。
          </div>
        )}

        {/* ── Content Area ── */}
        {loading ? (
          <div className="ch-loading">
            <div className="ch-loading__spinner" />
          </div>
        ) : filteredTasks.length > 0 ? (
          viewMode === 'card' ? (
            /* ─── Card View ─── */
            <div className="ch-grid">
              {filteredTasks.map((task, idx) => {
                const cover = getTaskCover(task);
                return (
                  <div
                    key={task.task_id || task.id}
                    className={`ch-card-enter ch-card ${task.status === 'queued' || task.status === 'running' ? 'ch-card--generating' : ''}`}
                    style={{ animationDelay: `${Math.min(idx * 60, 600)}ms` }}
                    onClick={() => openTaskDetail(task)}
                  >
                    {/* Cover */}
                    <div className="ch-card__cover">
                      {/* Generating Overlay — 仅覆盖封面图区域 */}
                      {(task.status === 'queued' || task.status === 'running') && (
                        <div className="ch-card__generating-overlay">
                          <Loader2 className="ch-card__generating-icon" size={24} />
                          <span className="ch-card__generating-text">创作中...</span>
                        </div>
                      )}
                      {cover ? (
                        <img src={cover} alt={getTaskTitle(task)} />
                      ) : (
                        <div className="ch-card__cover-empty">暂无配图</div>
                      )}
                      <div className="ch-card__cover-overlay" />

                      {isMockTask(task) && (
                        <span className="ch-card__mock-badge">MOCK</span>
                      )}

                      {/* Hover actions */}
                      <div className="ch-card__actions">
                        <button
                          type="button"
                          title="删除"
                          className="ch-card__action-btn ch-card__action-btn--delete"
                          onClick={(e) => handleDeleteClick(e, task)}
                        >
                          <Trash2 style={{ width: 15, height: 15 }} />
                        </button>
                        {(task.status === 'failed' || task.status === 'partial_failed') && (
                          <button
                            type="button"
                            title="重做"
                            className="ch-card__action-btn ch-card__action-btn--redo"
                            onClick={(e) => handleRedoClick(e, task)}
                          >
                            <RotateCcw style={{ width: 15, height: 15 }} />
                          </button>
                        )}
                      </div>

                      {/* CTA on hover */}
                      <div className="ch-card__cta">
                        <div className="ch-card__cta-inner">
                          <Eye style={{ width: 15, height: 15 }} />
                          查看详情
                        </div>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="ch-card__body">
                      <h3 className="ch-card__title">{getTaskTitle(task)}</h3>
                      <div className="ch-card__meta">
                        <StatusBadge status={task.status} />
                        <span className="ch-card__time">
                          <Clock style={{ width: 11, height: 11, display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />
                          {formatTime(task.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* ─── List View ─── */
            <div className="ch-list-view">
              {sortedListTasks.map((task, idx) => {
                const cover = getTaskCover(task);
                return (
                  <div
                    key={task.task_id || task.id}
                    className="ch-list-item"
                    style={{ animationDelay: `${Math.min(idx * 80, 600)}ms` }}
                  >
                    <div className="ch-list-item__date">
                      {formatSmartTime(task.created_at)}
                    </div>
                    <div
                      className={`ch-list-card ${task.status === 'queued' || task.status === 'running' ? 'ch-list-card--generating' : ''}`}
                      onClick={() => openTaskDetail(task)}
                    >
                      {/* Generating Overlay — 列表视图缩略图覆盖 */}
                      {(task.status === 'queued' || task.status === 'running') && (
                        <div className="ch-card__generating-overlay" style={{ borderRadius: 'var(--ch-radius-md)', position: 'relative', width: 120, minWidth: 120, height: 100 }}>
                          <Loader2 className="ch-card__generating-icon" size={20} />
                          <span className="ch-card__generating-text" style={{ fontSize: '12px' }}>创作中...</span>
                        </div>
                      )}
                      {/* Thumbnail */}
                      <div className="ch-list-card__thumb">
                        {cover ? (
                          <img src={cover} alt={getTaskTitle(task)} />
                        ) : (
                          <div className="ch-list-card__thumb-empty">暂无图</div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="ch-list-card__content">
                        <div>
                          <h4 className="ch-list-card__title">{getTaskTitle(task)}</h4>
                          <p className="ch-list-card__desc">{task.copywriting || task.topic || '暂无描述'}</p>
                        </div>
                        <div className="ch-list-card__footer">
                          <StatusBadge status={task.status} />
                          <div className="ch-list-card__actions">
                            <button
                              type="button"
                              className="ch-list-action ch-list-action--delete"
                              onClick={(e) => handleDeleteClick(e, task)}
                            >
                              <Trash2 style={{ width: 13, height: 13 }} />
                              删除
                            </button>
                            {(task.status === 'failed' || task.status === 'partial_failed') && (
                              <button
                                type="button"
                                className="ch-list-action ch-list-action--redo"
                                onClick={(e) => handleRedoClick(e, task)}
                              >
                                <RotateCcw style={{ width: 13, height: 13 }} />
                                重做
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* ─── Empty State ─── */
          <div className="ch-empty">
            <div className="ch-empty__icon">
              <Inbox style={{ width: 26, height: 26, color: 'var(--ch-accent)' }} />
            </div>
            <h3 className="ch-empty__title">
              {searchQuery ? '未找到匹配结果' : '暂无历史任务'}
            </h3>
            <p className="ch-empty__desc">
              {searchQuery ? '试试其他关键词' : '生成内容后会自动出现在这里'}
            </p>
          </div>
        )}
      </div>

      {/* ── Delete Confirm ── */}
      <DeleteConfirmDialog
        task={deleteTarget}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* ── Task Detail Modal ── */}
      <TaskDetailModal
        task={selectedTask}
        pages={selectedTaskPages}
        loading={detailLoading}
        onClose={closeDetail}
        onRefresh={refreshDetail}
        onPreview={(indexOrAction) => {
          if (typeof indexOrAction === 'number') {
            const imgs = normalizeTaskPages(selectedTask)
              .filter(p => p.image?.url)
              .map(p => p.image.url);
            handlePreviewImage(indexOrAction, imgs);
          } else {
            setPreviewOpen(true);
          }
        }}
        onDownload={() => downloadTaskAssets(selectedTask)}
        onCopy={copyTaskText}
        onDelete={handleDeleteClick ? (task) => handleDeleteClick({ stopPropagation: () => { } }, task) : undefined}
        onRegenerateImage={handleRegenerateImage}
        token={token}
        showMessage={showMessage}
      />

      {/* ── Task Preview Modal ── */}
      <TaskPreviewModal
        task={selectedTask}
        pages={selectedTaskPages}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />

      {/* ── Image Preview Modal ── */}
      <ImagePreviewModal
        images={previewImages.length > 0 ? previewImages : null}
        initialIndex={previewIndex}
        onClose={() => { setPreviewImages([]); setPreviewIndex(0); }}
      />

      {/* ── Draft Wizard Modal ── */}
      <ContentWizardModal
        open={Boolean(draftWizard)}
        onClose={() => setDraftWizard(null)}
        hotspot={draftWizard ? {
          topic: draftWizard.topic,
          title: draftWizard.topic,
          task_id: draftWizard.task_id,
          outline: draftWizard.outline,
        } : null}
        token={token}
        onComplete={() => {
          setDraftWizard(null);
          fetchHistory();
          showMessage('success', '图片生成完成！');
        }}
      />
    </div >
  );
};

export default ContentHistory;
